/**
 * Email Account Wizard
 * Multi-step wizard modal for adding email accounts
 */
import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/base/buttons/button";
import { X, Check } from "@untitledui/icons";
import { EmailProvider, type WizardState } from "@/types/api/email";
import { EmailEntryStep } from "./email-entry-step";
import { GmailOAuthStep } from "./gmail-oauth-step";
import { OutlookOAuthStep } from "./outlook-oauth-step";
import { ImapConfigStep } from "./imap-config-step";
import { SuccessStep } from "./success-step";
import { cx } from "@/utils/cx";

interface EmailAccountWizardProps {
    onComplete: (email?: string) => void;
    onCancel: () => void;
}

type WizardStep = "email" | "connect" | "success";

const stepOrder: WizardStep[] = ["email", "connect", "success"];

export const EmailAccountWizard = ({
    onComplete,
    onCancel,
}: EmailAccountWizardProps) => {
    const { t } = useTranslation(["pages:email-settings"]);

    const [currentStep, setCurrentStep] = useState<WizardStep>("email");
    const [wizardState, setWizardState] = useState<WizardState>({
        currentStep: "email-entry",
        email: "",
        provider: null,
        displayName: "",
        allowedDomains: [],
        accountId: null,
        imapPreset: null,
        imapHost: "",
        imapPort: 993,
        imapUsername: "",
        imapPassword: "",
        imapUseSsl: true,
        smtpHost: "",
        smtpPort: 587,
        smtpUseSsl: false,
    });

    const resetWizard = useCallback(() => {
        setCurrentStep("email");
        setWizardState({
            currentStep: "email-entry",
            email: "",
            provider: null,
            displayName: "",
            allowedDomains: [],
            accountId: null,
            imapPreset: null,
            imapHost: "",
            imapPort: 993,
            imapUsername: "",
            imapPassword: "",
            imapUseSsl: true,
            smtpHost: "",
            smtpPort: 587,
            smtpUseSsl: false,
        });
    }, []);

    const handleCancel = useCallback(() => {
        resetWizard();
        onCancel();
    }, [resetWizard, onCancel]);

    const handleEmailSubmit = useCallback((email: string, displayName: string, provider: EmailProvider) => {
        setWizardState((prev) => ({
            ...prev,
            email,
            displayName,
            provider,
        }));
        setCurrentStep("connect");
    }, []);

    const handleBack = useCallback(() => {
        const currentIndex = stepOrder.indexOf(currentStep);
        if (currentIndex > 0) {
            setCurrentStep(stepOrder[currentIndex - 1]);
        }
    }, [currentStep]);

    const handleConnectSuccess = useCallback(() => {
        setCurrentStep("success");
    }, []);

    const handleFinish = useCallback(() => {
        const email = wizardState.email;
        resetWizard();
        onComplete(email);
    }, [resetWizard, onComplete, wizardState.email]);

    const handleAddAnother = useCallback(() => {
        resetWizard();
    }, [resetWizard]);

    const currentStepIndex = stepOrder.indexOf(currentStep);

    return (
        <div className="rounded-xl bg-primary shadow-xs ring-1 ring-secondary">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-secondary px-6 py-4">
                <h2 className="text-lg font-semibold text-primary">
                    {t("wizard.title")}
                </h2>
                <Button
                    onClick={handleCancel}
                    color="tertiary"
                    size="sm"
                    iconLeading={X}
                    aria-label="Close"
                />
            </div>

            {/* Step Indicator */}
            <div className="border-b border-secondary px-6 py-4">
                <div className="flex items-center justify-between">
                    {stepOrder.map((step, index) => (
                        <div key={step} className="flex items-center">
                            {/* Step Circle */}
                            <div
                                className={cx(
                                    "flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors",
                                    index <= currentStepIndex
                                        ? "bg-brand-solid text-fg-white"
                                        : "bg-tertiary text-tertiary"
                                )}
                            >
                                {index < currentStepIndex ? (
                                    <Check className="h-4 w-4" />
                                ) : (
                                    index + 1
                                )}
                            </div>
                            {/* Step Label */}
                            <span
                                className={cx(
                                    "ml-2 text-sm",
                                    index === currentStepIndex
                                        ? "font-medium text-primary"
                                        : "text-tertiary"
                                )}
                            >
                                {t(`wizard.steps.${step}`)}
                            </span>
                            {/* Connector Line */}
                            {index < stepOrder.length - 1 && (
                                <div
                                    className={cx(
                                        "mx-4 h-0.5 w-12",
                                        index < currentStepIndex
                                            ? "bg-brand-solid"
                                            : "bg-tertiary"
                                    )}
                                />
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Content */}
            <div className="p-6">
                {currentStep === "email" && (
                    <EmailEntryStep
                        initialEmail={wizardState.email}
                        initialDisplayName={wizardState.displayName}
                        onSubmit={handleEmailSubmit}
                        onCancel={handleCancel}
                    />
                )}

                {currentStep === "connect" && wizardState.provider === EmailProvider.GMAIL && (
                    <GmailOAuthStep
                        email={wizardState.email}
                        displayName={wizardState.displayName}
                        onBack={handleBack}
                        onSuccess={handleConnectSuccess}
                    />
                )}

                {currentStep === "connect" && wizardState.provider === EmailProvider.OUTLOOK && (
                    <OutlookOAuthStep
                        email={wizardState.email}
                        displayName={wizardState.displayName}
                        onBack={handleBack}
                        onSuccess={handleConnectSuccess}
                    />
                )}

                {currentStep === "connect" && wizardState.provider === EmailProvider.IMAP_SMTP && (
                    <ImapConfigStep
                        email={wizardState.email}
                        displayName={wizardState.displayName}
                        onBack={handleBack}
                        onSuccess={handleConnectSuccess}
                    />
                )}

                {currentStep === "success" && (
                    <SuccessStep
                        onViewAccounts={handleFinish}
                        onAddAnother={handleAddAnother}
                    />
                )}
            </div>
        </div>
    );
};
